/**
 * Copy Calculator Result to Clipboard
 *
 * Formats calculator results as a shareable text block
 * and copies to clipboard with toast feedback.
 */

import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import type { RdwDraft } from '@/lib/rdwClient';
import type { BpmResult, ClassificationResult } from '../lib/types';
import { getClassificationLabel, getConfidenceLabel } from '../lib/classifyVehicle';
import { formatEuro, formatPlate, formatPct, formatDateTime } from '../lib/format';

interface CopyResultParams {
  rdwData: RdwDraft;
  bpm: BpmResult | null;
  classification: ClassificationResult | null;
  maxBuyPrice: number | null;
  expectedSellingPrice: number | null;
  targetMarginPct: number;
}

/**
 * Build formatted text for clipboard
 * Optimized for sharing via WhatsApp, email, or notes
 */
export function buildResultText(params: CopyResultParams): string {
  const { rdwData, bpm, classification, maxBuyPrice, expectedSellingPrice, targetMarginPct } =
    params;

  const lines: string[] = [];

  // Header with timestamp
  lines.push('Snelle Calculator (modcar)');
  lines.push(`Datum: ${formatDateTime()}`);
  lines.push('');

  // Vehicle info - compact format
  lines.push(`Kenteken: ${formatPlate(rdwData.plate)}`);
  const vehicleDesc = [rdwData.make, rdwData.model].filter(Boolean).join(' ');
  const year = rdwData.firstRegDate ? rdwData.firstRegDate.substring(0, 4) : null;
  const fuelType = rdwData.fuelType;
  const vehicleLine = [vehicleDesc, year, fuelType].filter(Boolean).join(' • ');
  if (vehicleLine) {
    lines.push(vehicleLine);
  }
  lines.push('');

  // BPM info
  if (bpm && (bpm.grossBpm !== null || bpm.restBpm !== null)) {
    lines.push('BPM:');
    if (bpm.grossBpm !== null) {
      lines.push(`- Bruto BPM: ${formatEuro(bpm.grossBpm)}`);
    }
    if (bpm.depreciationPct !== null) {
      const ageInfo = bpm.monthsSinceFirstUse !== null ? ` (${bpm.monthsSinceFirstUse} mnd)` : '';
      lines.push(`- Afschrijving: ${formatPct(bpm.depreciationPct)}${ageInfo}`);
    }
    if (bpm.restBpm !== null) {
      lines.push(`- Rest-BPM: ${formatEuro(bpm.restBpm)}`);
    }
    lines.push('');
  }

  // Classification
  if (classification) {
    lines.push('BTW/Marge:');
    lines.push(
      `- Waarschijnlijk: ${getClassificationLabel(classification.classification)} (${getConfidenceLabel(classification.confidence).toLowerCase()})`
    );
    lines.push('- Indicatie — controleer herkomst voertuig');
    lines.push('');
  }

  // Max buy price
  if (maxBuyPrice !== null && expectedSellingPrice !== null) {
    lines.push('Max inkoopprijs:');
    lines.push(`- Verkoop: ${formatEuro(expectedSellingPrice)}`);
    lines.push(`- Marge: ${targetMarginPct}%`);
    if (bpm?.restBpm !== null && bpm.restBpm > 0) {
      lines.push(`- Rest-BPM: ${formatEuro(bpm.restBpm)}`);
    }
    lines.push(`- Max inkoop: ${formatEuro(maxBuyPrice)}`);
    lines.push('');
  }

  // Footer disclaimer
  lines.push('⚠️ Dit is een indicatie. Controleer gegevens bij verkoper.');

  return lines.join('\n');
}

/**
 * Copy calculator results to clipboard with toast feedback
 */
export async function copyCalculatorResult(params: CopyResultParams): Promise<void> {
  try {
    const text = buildResultText(params);
    await navigator.clipboard.writeText(text);
    toast.success('Resultaten gekopieerd naar klembord');
  } catch (error) {
    logger.error('[copyCalculatorResult] Failed to copy:', error);
    toast.error('Kon resultaten niet kopiëren');
  }
}
